import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Breadcrumb from "components/Common/Breadcrumb";
import DashboardLoader from "components/Loader/DashboardLoader";

// API Service for Customers
const customerApiService = {
  // Check if phone number exists
  checkPhoneExists: async (phone) => {
    try {
      const response = await fetch(`https://qwikbistro.shop/userProfile/`, {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
        },
      });
      const users = await response.json();
      return users.find(user => user.phone === phone);
    } catch (error) {
      console.error('Error checking phone:', error);
      throw error;
    }
  },

  // Create new customer
  createCustomer: async (formData) => {
    try {
      const response = await fetch(`https://qwikbistro.shop/userProfile/new`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
        },
        body: formData,
      });
      return await response.json();
    } catch (error) {
      console.error('Error creating customer:', error);
      throw error;
    }
  },

  // Update customer with QB User ID
  updateCustomer: async (id, data) => {
    try {
      const response = await fetch(`https://qwikbistro.shop/userProfile/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_QWIKBISTRO_TOKEN}`,
        },
        body: JSON.stringify(data),
      });
      return await response.json();
    } catch (error) {
      console.error('Error updating customer:', error);
      throw error;
    }
  },
};

const CreateCustomerPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password_hash: "",
    address: "",
    role: "customer",
    note: "",
    photo: null,
    dob: "",
    city: "",
  });

  // Format customer ID
  const formatCustomerId = (id) => {
    return `QB-CU-${String(id).padStart(4, "0")}`;
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle image upload
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, photo: file }));

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate required fields
      if (!formData.name || !formData.phone) {
        toast.error("Name and phone number are required");
        setLoading(false);
        return;
      }

      // Check if phone number already exists
      const existingUser = await customerApiService.checkPhoneExists(formData.phone);
      if (existingUser) {
        setShowDuplicateModal(true);
        setLoading(false);
        return;
      }

      // Prepare FormData
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append("email", formData.email || "");
      formDataToSend.append("phone", formData.phone);
      formDataToSend.append("password_hash", formData.password_hash || "123456");
      formDataToSend.append("address", formData.address || "");
      formDataToSend.append("role", formData.role);
      formDataToSend.append("note", formData.note || "");
      formDataToSend.append("city", formData.city || "");

      if (formData.dob) {
        formDataToSend.append("dob", new Date(formData.dob).toISOString());
      }

      if (formData.photo) {
        formDataToSend.append("photo", formData.photo);
      }

      // Create customer
      const createdCustomer = await customerApiService.createCustomer(formDataToSend);

      // Generate and update QB User ID
      const qbUserId = formatCustomerId(createdCustomer.id);
      await customerApiService.updateCustomer(createdCustomer.id, {
        qb_user_id: qbUserId
      });

      toast.success("Customer created successfully");

      // Navigate to customer details or customers list
      setTimeout(() => {
        // navigate(`/customers/${createdCustomer.id}`);
        navigate(`/all-customers`);
      }, 1500);

    } catch (error) {
      console.error("Error creating customer:", error);
      toast.error("Failed to create customer");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Customers" breadcrumbItem="Create New Customer" />

        {/* Duplicate Phone Modal */}
        <div className={`modal fade ${showDuplicateModal ? 'show d-block' : ''}`} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Phone Number Exists</h5>
                <button type="button" className="btn-close" onClick={() => setShowDuplicateModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>This phone number is already registered. Please use a different phone number.</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setShowDuplicateModal(false);
                    setFormData(prev => ({ ...prev, phone: "" }));
                  }}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                {/* <h4 className="card-title mb-4">Create New Customer</h4> */}

                <form onSubmit={handleSubmit}>
                  {/* Profile Image Section */}
                  <div className="row mb-4">
                    <div className="col-md-12">
                      <div className="text-center mb-3">
                        <div className="position-relative d-inline-block">
                          <div className="avatar-xl">
                            {imagePreview ? (
                              <img
                                src={imagePreview}
                                alt="Preview"
                                className="rounded-circle img-thumbnail"
                                style={{ width: '120px', height: '120px', objectFit: 'cover' }}
                              />
                            ) : (
                              <div className="avatar-xl bg-light rounded-circle d-flex align-items-center justify-content-center">
                                <i className="fas fa-user text-muted" style={{ fontSize: '3rem' }}></i>
                              </div>
                            )}
                          </div>
                          <div className="mt-3">
                            <label className="btn btn-primary">
                              <i className="fas fa-camera me-1"></i> Upload Photo
                              <input
                                type="file"
                                accept="image/*"
                                className="d-none"
                                onChange={handleImageChange}
                              />
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Customer Information */}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Full Name <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          required
                          placeholder="Enter full name"
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Role</label>
                        <select
                          className="form-select"
                          value={formData.role}
                          onChange={(e) => handleInputChange('role', e.target.value)}
                        >
                          <option value="customer">Customer</option>
                          <option value="vendor">Vendor</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Email Address</label>
                        <input
                          type="email"
                          className="form-control"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          placeholder="Enter email address"
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Phone Number <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          required
                          placeholder="Enter phone number"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Password</label>
                        <input
                          type="password"
                          className="form-control"
                          value={formData.password_hash}
                          onChange={(e) => handleInputChange('password_hash', e.target.value)}
                          placeholder="Leave empty for default"
                        />
                        <small className="text-muted">Default: 123456 (if left empty)</small>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Date of Birth</label>
                        <input
                          type="date"
                          className="form-control"
                          value={formData.dob}
                          onChange={(e) => handleInputChange('dob', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Notes</label>
                        <textarea
                          className="form-control"
                          rows="3"
                          value={formData.note}
                          onChange={(e) => handleInputChange('note', e.target.value)}
                          placeholder="Additional notes about the customer..."
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Address</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.address}
                          onChange={(e) => handleInputChange('address', e.target.value)}
                          placeholder="Enter address"
                        />
                      </div>
                    </div>

                    {/* <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">City</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.city}
                          onChange={(e) => handleInputChange('city', e.target.value)}
                          placeholder="Enter city"
                        />
                      </div>
                    </div> */}
                  </div>



                  {/* Form Actions */}
                  <div className="row">
                    <div className="col-12">
                      <div className="d-flex justify-content-between">


                        <div className="d-flex gap-2">


                          <button
                            type="submit"
                            className="btn btn-success"
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <i className="fas fa-spinner fa-spin me-1"></i>
                                Creating...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-user-plus me-1"></i>
                                Create Customer
                              </>
                            )}
                          </button>

                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => {
                              setFormData({
                                name: "",
                                email: "",
                                phone: "",
                                password_hash: "",
                                address: "",
                                role: "customer",
                                note: "",
                                photo: null,
                                dob: "",
                                city: "",
                              });
                              setImagePreview(null);
                            }}
                            disabled={loading}
                          >
                            <i className="fas fa-redo me-1"></i>
                            {/* Reset Form */}
                          </button>

                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>


      </div>
    </div>
  );
};

export default CreateCustomerPage;